/**
 * Uptime-Kuma monitor hooks — list/create/delete, via the @mgmt control plane
 * (serviceApiV3, base …/api/v3). These monitors are GLOBAL (org-level): the
 * list endpoint takes no project_id, so nothing here is project-scoped.
 *
 * Response shape — VERIFIED against @mgmt `modules/monitoring/controller.go`:
 * the controller answers with `response.OK/Created` which wrap the payload in
 * `{ status, data }` (`pkg/response/response.go` SuccessResponse). So despite
 * being a "raw" Kuma array on the gateway side, the proxy DOES envelope it —
 * unwrap via `res.data.data`. Delete returns a MessageResponse `{ status,
 * message }` (no data).
 *
 * Live up/down, uptime % and response-time are NOT exposed by this REST surface
 * (only the `active` enabled flag). Those live in Prometheus (`monitor_status`,
 * `monitor_response_time`) and are a backend follow-up — do not synthesize them.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** Control-plane envelope: `response.OK/Created` → `{ status, data }`. */
interface Envelope<T> {
  status?: number;
  data: T;
  message?: string;
}

/** Monitor check types Kuma supports through this surface. */
export type KumaMonitorType = 'http' | 'keyword' | 'tcp' | 'ping' | 'dns';

/** The subset of a Kuma monitor the control plane exposes. */
export interface KumaMonitor {
  id: number;
  name: string;
  url: string;
  type: string;
  active: boolean;
  interval: number;
}

export interface CreateKumaMonitorInput {
  name: string;
  url: string;
  type?: KumaMonitorType;
  /** Seconds between checks. Backend defaults to 60 when omitted. */
  interval?: number;
  /** Required when `type === 'keyword'` — the string to find in the response. */
  keyword?: string;
}

export const kumaKeys = {
  all: ['kuma'] as const,
  monitors: ['kuma', 'monitors'] as const,
};

const MONITORS_PATH = '/monitoring/monitors';

/** List all Kuma monitors (org-wide inventory). */
export function useKumaMonitors() {
  return useQuery<KumaMonitor[]>({
    queryKey: kumaKeys.monitors,
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<KumaMonitor[]>>(MONITORS_PATH);
      return res.data?.data ?? [];
    },
  });
}

/** Create a monitor. Returns the new monitor's id. */
export function useCreateKumaMonitor(
  options?: UseMutationOptions<{ id: number }, Error, CreateKumaMonitorInput>,
) {
  const qc = useQueryClient();
  return useMutation<{ id: number }, Error, CreateKumaMonitorInput>({
    mutationFn: async (input) => {
      const res = await serviceApiV3.post<Envelope<{ id: number }>>(MONITORS_PATH, input);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: kumaKeys.monitors });
      options?.onSuccess?.(...args);
    },
  });
}

/* ────────────────────────────────────────────────────────────────────────
 * Full-parity surface: create-form schema, raw create, bulk, stats, events.
 * ──────────────────────────────────────────────────────────────────────── */

/** One field of the Add-Monitor form (from `GET /monitoring/monitor-types`). */
export interface MonitorFieldSpec {
  key: string;
  label: string;
  kind: 'text' | 'number' | 'bool' | 'select' | 'textarea' | 'keyvalue';
  required?: boolean;
  default?: unknown;
  options?: string[];
  help?: string;
}
/** One monitor type + its fields (drives the dynamic create form). */
export interface MonitorTypeSpec {
  type: string;
  label: string;
  fields: MonitorFieldSpec[];
}

/** Type + field schema for rendering the full Add-Monitor form. */
export function useMonitorTypes() {
  return useQuery<MonitorTypeSpec[]>({
    queryKey: ['kuma', 'monitor-types'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<MonitorTypeSpec[]>>('/monitoring/monitor-types');
      return res.data?.data ?? [];
    },
  });
}

/**
 * Create from a RAW Kuma object (any type). The backend passes it through and
 * fills required defaults (conditions, intervals, accepted_statuscodes). Use
 * with the dynamic form built from useMonitorTypes.
 */
export function useCreateKumaMonitorRaw(
  options?: UseMutationOptions<{ id: number }, Error, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation<{ id: number }, Error, Record<string, unknown>>({
    mutationFn: async (monitor) => {
      const res = await serviceApiV3.post<Envelope<{ id: number }>>(MONITORS_PATH, monitor);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: kumaKeys.monitors });
      options?.onSuccess?.(...args);
    },
  });
}

export interface BulkAddResult {
  added: number;
  failed: number;
  ids?: number[];
  errors?: string[];
}

/** Create many raw monitor objects in one call (one Kuma login). */
export function useBulkCreateKumaMonitors(
  options?: UseMutationOptions<BulkAddResult, Error, Record<string, unknown>[]>,
) {
  const qc = useQueryClient();
  return useMutation<BulkAddResult, Error, Record<string, unknown>[]>({
    mutationFn: async (monitors) => {
      const res = await serviceApiV3.post<Envelope<BulkAddResult>>(`${MONITORS_PATH}/bulk`, { monitors });
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: kumaKeys.monitors });
      options?.onSuccess?.(...args);
    },
  });
}

/** Quick Stats header (Up/Down/Maintenance/Pending/Pause). */
export interface MonitorStats {
  up: number;
  down: number;
  maintenance: number;
  pending: number;
  pause: number;
  active: number;
  total: number;
}

/** Up/Down/Maintenance/Pending/Pause counts for the dashboard header. */
export function useMonitorStats() {
  return useQuery<MonitorStats>({
    queryKey: ['kuma', 'stats'],
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<MonitorStats>>('/monitoring/stats');
      return res.data.data;
    },
  });
}

/** One state-change event (Kuma important heartbeat) — carries the message. */
export interface MonitorEvent {
  monitor_id: number;
  name: string;
  /** 0=down, 1=up, 2=pending, 3=maintenance. */
  status: number;
  time: string;
  msg: string;
}

/** Recent state-change events across monitors (newest first). */
export function useMonitorEvents(limit = 50) {
  return useQuery<MonitorEvent[]>({
    queryKey: ['kuma', 'events', limit],
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<MonitorEvent[]>>('/monitoring/events', {
        params: { limit },
      });
      return res.data?.data ?? [];
    },
  });
}

/** Delete a monitor by id. */
export function useDeleteKumaMonitor(
  options?: UseMutationOptions<void, Error, number>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await serviceApiV3.delete(`${MONITORS_PATH}/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: kumaKeys.monitors });
      options?.onSuccess?.(...args);
    },
  });
}
